import { v4 as uuidv4 } from 'uuid'
import { user } from 'ampersand-app'
import request from '@/modules/base/util/request'
import { urls } from '@/modules/banking/config'
import { BookingService } from '@/modules/banking/services'
import { createLogEntryData } from './_helpers'
import { generateProcessId, getProcessIdHeader } from '@/modules/banking/_helpers/generateProcessId'
import config from '@/config'
import moment from 'moment'
import { pollTransaction } from '@/redux/ducks/banking/_helpers'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'

const apiRoot = urls.bankingApiHost
const API_VERSION = '1'
const baseUrl = `${config.resourceHost}`

const isLoggingEnabled = () => {
  return user.get('bankingLogsEnabled')
}

const getHeaders = (authtoken, processId) => {
  let headers = {
    'api-version': API_VERSION,
    ...(authtoken ? { authtoken } : {}),
  }

  if (isLoggingEnabled()) {
    headers = {
      ...headers,
      requestId: uuidv4(),
      ...getProcessIdHeader(processId),
      loglevel: 'debug',
    }
  }

  return headers
}

const addLogEntry = (response, { method }) => {
  if (!isLoggingEnabled()) {
    return new Promise(resolve => { resolve() })
  }

  const logEntry = createLogEntryData(response, { method })
  return BookingService.logging.add(logEntry)
}

/**
 *
 * @param {string} url request url
 * @param {object} fetchOptions fetch options
 */
const sendRequest = (url, fetchOptions) => {
  return new Promise((resolve, reject) => {
    const { method } = fetchOptions
    let originalResponse

    request(url, fetchOptions)
      .then(response => {
        originalResponse = response
        return addLogEntry(response.response, { method })
      })
      .then(() => {
        resolve(originalResponse)
      })
      .catch((error, response) => {
        reject(error)
      })
  })
}

export const BankingService = {
  psd2: {
    callbacks: {
      contactChange: (authtoken, data, processId) => {
        return sendRequest(`${apiRoot}/psd2/Callbacks/contactchange`, {
          method: 'POST',
          headers: getHeaders(authtoken, processId),
          data,
        })
      },
      pin: (authtoken, data, processId) => {
        return sendRequest(`${apiRoot}/psd2/Callbacks/pin`, {
          method: 'POST',
          headers: {
            ...getHeaders(authtoken, processId),
            'content-type': 'application/json',
          },
          data,
        })
      },
      tan: (authtoken, data, processId) => {
        return sendRequest(`${apiRoot}/psd2/Callbacks/tan`, {
          method: 'POST',
          headers: getHeaders(authtoken, processId),
          data,
        })
      },
      universal: (authtoken, data, processId) => {
        return sendRequest(`${apiRoot}/psd2/Callbacks/universal`, {
          method: 'POST',
          headers: getHeaders(authtoken, processId),
          data,
        })
      },
    },
    contactSetup: {
      /**
             * get state of pbb transaction
             *
             * @param authtoken number
            */
      getState: (authtoken, processId) => {
        return sendRequest(`${apiRoot}/psd2/ContactSetupData/state`, {
          method: 'GET',
          headers: getHeaders(authtoken, processId),
        })
      },
      postData: (authtoken, data, processId) => {
        return sendRequest(`${apiRoot}/psd2/ContactSetupData/data`, {
          method: 'POST',
          headers: getHeaders(authtoken, processId),
          data,
        })
      },
    },
    agreements: {
      get: (token, processId) => {
        return sendRequest(`${apiRoot}/Agreements`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'api-version': API_VERSION,
            ...getHeaders(null, processId),
          },
        })
      },
      approve: (token, processId) => {
        return sendRequest(`${apiRoot}/psd2/Agreements`, {
          method: 'POST',
          headers: {
            authtoken: token,
            'api-version': API_VERSION,
            ...getHeaders(null, processId),
          },
        })
      },
    },
  },
  getBankContactSettings: (contactId, accountId) => {
    return new Promise((resolve, reject) => {
      request(urls.getBankContactSettings(contactId), { method: 'GET', auth: true, data: { accountId } })
        .then(response => {
          const { body: { data: settingsResult } } = response
          resolve(settingsResult)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  canBeDeleted: (contactId) => {
    return new Promise((resolve, reject) =>
      request(urls.checkHasAssignments(contactId), { method: 'GET', auth: true, isConcurrent: true })
        .then(response => {
          const { body: { data: { hasAssignments } } } = response
          resolve(!hasAssignments)
        })
        .catch((error) => {
          reject(error)
        }),
    )
  },
  resendMandate: (mandateId) => {
    return new Promise((resolve, reject) => {
      return request(`${baseUrl}banking/directDebit/mandates/resend/${mandateId}`, {
        auth: true,
        isConcurrent: true,
      })
        .then((result) => resolve(result))
        .catch(error => reject(error))
    })
  },

  getCustomerMandates: (customerId, paranoid = false) => {
    return new Promise((resolve, reject) => {
      return request(`${baseUrl}banking/directDebit/mandates/customer/${customerId}?paranoid=${paranoid}`, {
        auth: true,
        isConcurrent: true,
      })
        .then((result) => {
          const { body: { data } } = result
          resolve(data)
        })
        .catch(error => reject(error))
    })
  },

  getMandate: (mandateId) => {
    return new Promise((resolve, reject) => {
      return request(`${baseUrl}banking/directDebit/mandates/${mandateId}`, {
        auth: true,
        isConcurrent: true,
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch(error => reject(error))
    })
  },

  cancelMandate: (mandateId) => {
    return new Promise((resolve, reject) => {
      return request(`${baseUrl}banking/directDebit/mandates/${mandateId}/cancel`, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
      })
        .then((result) => resolve(result))
        .catch(error => reject(error))
    })
  },

  requestNewMandate: (customerId) => {
    return new Promise((resolve, reject) => {
      return request(`${baseUrl}banking/directDebit/mandates/request`, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        data: {
          customerId,
          mandateType: 'recurring',
        },
      })
        .then((result) => {
          const { body: { data } } = result
          resolve(data)
        })
        .catch(error => reject(error))
    })
  },

  getDirectDebitTransactionByInvoiceId: (invoiceId) => {
    return new Promise((resolve, reject) => {
      return request(`${baseUrl}banking/directDebit/transactions/invoice/${invoiceId}`, {
        auth: true,
        isConcurrent: true,
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch(error => reject(error))
    })
  },

  getNextValueBankingDay: (valueDate = moment()) => {
    const valueDateString = valueDate.format('YYYY-MM-DD')
    return new Promise((resolve, reject) => {
      return sendRequest(`${baseUrl}banking/directDebit/next-value-banking-day?valueDate=${valueDateString}`, {
        method: 'GET',
        auth: true,
        isConcurrent: true,
      }).then(response => {
        const { body: { data } } = response
        resolve(data)
      })
        .catch(error => reject(error))
    })
  },

  deleteDebitTransaction: (debitDetailId) => {
    return new Promise((resolve, reject) => {
      return request(`${baseUrl}banking/directDebit/transactions/${debitDetailId}`, {
        method: 'DELETE',
        auth: true,
        isConcurrent: true,
      })
        .then(() => {
          resolve(true)
        })
        .catch(error => reject(error))
    })
  },
  getDebitTransaction: (debitDetailId) => {
    return new Promise((resolve, reject) => {
      return request(`${baseUrl}banking/directDebit/transactions/${debitDetailId}`, {
        auth: true,
        isConcurrent: true,
      })
        .then(response => {
          const { body: { data } } = response
          resolve(data)
        })
        .catch(error => reject(error))
    })
  },
  submitDebitTransaction: (debitDetailId) => {
    const processId = generateProcessId()

    return new Promise((resolve, reject) => {
      request(`${baseUrl}banking/directDebit/transactions/${debitDetailId}/submit`, {
        method: 'PUT',
        auth: true,
        isConcurrent: true,
      })
        .then(({ body: { data: transaction } }) => {
          return pollTransaction(transaction, 'bookingApi', processId)
        })
        .then((result) => {
          return request(`${baseUrl}banking/directDebit/transactions/${debitDetailId}/callback`, {
            auth: true,
            method: 'PUT',
            headers: { ...getProcessIdHeader(processId) },
            data: {
              result,
            },
          }).then(() => {
            resolve()
          })
        })
        .catch((error) => {
          if (error.body?.meta?.minDate && error.body?.meta?.maxDate) {
            toast.showToast({
              message: lang.debitTransactions.debitTransactionList.detail.errors.minDateAndMaxDate(error.body?.meta?.minDate[0].code, error.body?.meta?.maxDate[0].code),
              type: 'error',
            })
            reject(error)
          }
          if (error.body?.meta?.[0]?.code === 'BankingAPIError') {
            toast.showToast({
              message: error.body?.meta?.[0]?.message,
              type: 'error',
            })
          }
          return request(`${baseUrl}banking/directDebit/transactions/${debitDetailId}/callback`, {
            auth: true,
            method: 'PUT',
            headers: { ...getProcessIdHeader(processId) },
            data: {
              error: error?.body || error,
            },
          }).then(() => {
            resolve()
          }).catch(error => reject(error))
        })
    })
  },
  saveDebitTransaction: (debitDetailId, debitTransaction) => {
    return new Promise((resolve, reject) => {
      return request(`${baseUrl}banking/directDebit/transactions/${debitDetailId}`, {
        method: 'PUT',
        auth: true,
        isConcurrent: true,
        data: debitTransaction,
      })
        .then(() => {
          resolve(true)
        })
        .catch(error => reject(error))
    })
  },
}
